/*
 * Local Forge: Request a Quote integration.
 *
 * Reads whatever product options the APO app (MageWorx Advanced Product
 * Options) rendered on the page, keeps the SetuBridge "Request a Quote" button
 * locked until every option is chosen, and writes the choices into the quote
 * form's message box.
 *
 * Nothing here names an option. The first version hardcoded the four option
 * IDs of the engagement ring set, and APO mints new IDs for every option set,
 * so the button was dead on every other product. Labels, values and prices are
 * all read from the page instead.
 *
 * Decisions from the client, 11 Sep 2026:
 *   - every option is required, not only the ones APO marks with an asterisk
 *   - prices stay in the message exactly as shown, e.g. "Size: 9 (+$150.00 USD)"
 *   - a product with no options still gets a working button (one of a kind)
 *   - a product with the quote button is quote only: no Add to Cart
 *
 * Which products show the button is decided by the SetuBridge app's own
 * settings (collections and tags), not by this file.
 */
(() => {
  const OPTION = '#mw-apo-app-block .mw-option[data-option-id], .mw-apo-container .mw-option[data-option-id]';
  const QUOTE_BUTTON = '.cfp-prod-button, .cfp-prod-wrapper-detail button, .cfp-prod-wrapper-detail a';
  const NOTICE_TEXT = 'Please complete all required selections before requesting a quote.';

  // Belt and braces. The quote app already removes the whole /cart/add form on
  // a quote product, and on live (checked 11 Sep 2026, Middleton and Tibet, 5
  // loads) no Add to Cart appears at all. But APO sometimes draws an Add to
  // Cart of its own outside that form, which was visible on the draft, and the
  // theme's sticky bar carries the hidden price as $0.00. Whether that is a
  // race between the two apps or a reaction to this rule, hiding them makes the
  // outcome the same every load. Scoped to the main product so recommendation
  // cards and quick view keep working.
  const QUOTE_ONLY_CSS = `
    .main-product .mw-apo-add-to-cart-custom,
    .main-product .sticky_add_to_cart,
    .main-product .shopify-payment-button,
    .main-product .add_to_cart_button { display: none !important; }`;

  function makeQuoteOnly() {
    if (document.getElementById('local-forge-quote-only')) return;
    const style = document.createElement('style');
    style.id = 'local-forge-quote-only';
    style.textContent = QUOTE_ONLY_CSS;
    document.head.appendChild(style);
  }

  // The quote app prints this style server-side, only on products it matches,
  // so it says "quote product" before either app has rendered anything.
  if (document.getElementById('cfp-pdp-hide-price-ssr')) makeQuoteOnly();

  function clean(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  // An option APO has hidden (a dependent option whose condition is not met)
  // is not on offer, so it cannot be required.
  function isShown(option) {
    return option.getClientRects().length > 0 && !option.classList.contains('mw-hidden');
  }

  function optionLabel(option) {
    const strong = option.querySelector('.mw-label .mw-text--style-strong');
    const text = strong ? strong.textContent : option.querySelector('.mw-label, legend')?.textContent;
    return clean(text).replace(/\s*\*$/, '');
  }

  // The text of one chosen value, with its price if APO shows one.
  function chosenText(input) {
    const value = input.closest('.mw-option__value') || input.parentElement;
    const title = value?.querySelector('.mw-option__value-title');
    if (title && clean(title.textContent)) return clean(title.textContent);

    let text =
      clean(input.getAttribute('aria-label')) ||
      clean(value?.querySelector('img[alt]')?.alt) ||
      clean(value?.querySelector('.mw-option__value-text')?.textContent) ||
      clean(input.value);
    const price = clean(value?.querySelector('.mw-option__price')?.textContent);
    if (price && !text.includes(price)) text = `${text} ${price}`;
    return text;
  }

  function optionValue(option) {
    const chosen = [];

    option.querySelectorAll('select').forEach((select) => {
      if (select.disabled || !select.value) return;
      chosen.push(clean(select.options[select.selectedIndex]?.text));
    });

    option.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked').forEach((input) => {
      if (!input.disabled) chosen.push(chosenText(input));
    });

    option.querySelectorAll('input[type="text"], input[type="number"], input[type="date"], textarea').forEach((field) => {
      if (!field.disabled && clean(field.value)) chosen.push(clean(field.value));
    });

    return chosen.filter((text) => text && !/please select/i.test(text)).join(', ');
  }

  function readOptions() {
    return [...document.querySelectorAll(OPTION)]
      .filter(isShown)
      .map((option) => ({ label: optionLabel(option), value: optionValue(option) }));
  }

  function missingOptions() {
    return readOptions().filter((option) => !option.value);
  }

  function productTitle() {
    return clean(document.querySelector('h1, .product-title, [data-product-title]')?.textContent);
  }

  // The quote form is a cart: a customer can add several pieces, across page
  // loads, before sending one request. Each piece's choices are remembered by
  // product name, which is also how the form lists them.
  const STORE = 'local-forge-quote-selections';

  function loadChoices() {
    try {
      return JSON.parse(localStorage.getItem(STORE)) || {};
    } catch {
      return {};
    }
  }

  function rememberChoices() {
    const choices = loadChoices();
    choices[productTitle()] = readOptions()
      .filter((option) => option.value)
      .map((option) => `${option.label}: ${option.value}`);
    try {
      localStorage.setItem(STORE, JSON.stringify(choices));
    } catch {
      // Private browsing can refuse storage. The message then carries this page's piece only.
    }
  }

  function buildMessage() {
    const choices = loadChoices();
    const listed = [...document.querySelectorAll('.sb-cfp2-bskt-name')].map((el) => clean(el.textContent)).filter(Boolean);
    const pieces = [...new Set(listed.length ? listed : [productTitle()])];
    return pieces.map((name) => [`Product: ${name}`, ...(choices[name] || [])].join('\n')).join('\n\n');
  }

  function noticeFor(button) {
    const wrapper = button.closest('.cfp-prod-wrapper-detail') || button.parentElement;
    let notice = wrapper?.parentElement?.querySelector(':scope > .local-forge-quote-notice');
    if (!notice && wrapper) {
      notice = document.createElement('div');
      notice.className = 'local-forge-quote-notice';
      notice.style.marginTop = '10px';
      notice.style.fontSize = '12px';
      notice.style.color = '#7a3e32';
      notice.textContent = NOTICE_TEXT;
      wrapper.insertAdjacentElement('afterend', notice);
    }
    return notice;
  }

  function updateQuoteButton() {
    const buttons = document.querySelectorAll(QUOTE_BUTTON);
    if (buttons.length) makeQuoteOnly();
    watchQuoteForm();
    const locked = missingOptions().length > 0;

    buttons.forEach((button) => {
      const notice = noticeFor(button);
      button.style.pointerEvents = locked ? 'none' : '';
      button.style.opacity = locked ? '0.45' : '';
      if (locked) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
      if (notice) notice.style.display = locked ? 'block' : 'none';
    });
  }

  // A timer, not requestAnimationFrame: animation frames never fire in a tab
  // that is not being shown, so a product opened in a background tab would sit
  // with an unlocked button.
  let queued = false;
  function queueUpdate() {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      updateQuoteButton();
    }, 30);
  }

  document.addEventListener('change', queueUpdate, true);
  document.addEventListener('input', queueUpdate, true);
  document.addEventListener('click', (event) => {
    if (event.target.closest('#mw-apo-app-block, .mw-apo-container')) queueUpdate();
  });

  // Both apps render after page load and at their own pace, so watch for their
  // markup instead of guessing a delay.
  const renders = new MutationObserver(queueUpdate);
  renders.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => renders.disconnect(), 20000);
  queueUpdate();

  // A locked button ignores pointer clicks through pointer-events, but a
  // keyboard can still activate it. Stop that before the quote app sees it.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest(QUOTE_BUTTON);
      if (!button || !missingOptions().length) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      updateQuoteButton();
    },
    true
  );

  // The message box is on the form's second step, "Details", which the
  // customer reaches whenever they like. Fill it each time it comes into view,
  // unless they have written something of their own in it.
  let lastFilled = '';
  function fillMessage() {
    const field = [...document.querySelectorAll('textarea.sb-cfp2ms-textarea')].find(
      (item) => item.getClientRects().length > 0
    );
    if (!field) return;

    const message = buildMessage();
    if (!message || field.value === message) return;

    if (field.value === '') {
      // Empty because they wiped what we wrote, not because we have not
      // written yet. Leave it alone until the quote itself changes.
      if (lastFilled && lastFilled === message) return;
    } else if (field.value !== lastFilled) {
      return; // their own words
    }

    field.value = message;
    lastFilled = message;
    field.dispatchEvent(new Event('input', { bubbles: true }));
  }

  let watchingForm = false;
  function watchQuoteForm() {
    const root = document.getElementById('sb-cfp2ms-root');
    if (!root || watchingForm) return;
    watchingForm = true;
    new MutationObserver(() => setTimeout(fillMessage, 0)).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden']
    });
    // The step slides in, so its mutations land while the message box is still
    // off screen and a mutation-only trigger misses it. This costs one query
    // twice a second and makes the fill happen every time.
    setInterval(fillMessage, 500);
  }

  document.addEventListener('click', (event) => {
    if (!event.target.closest(QUOTE_BUTTON)) return;
    if (missingOptions().length) return;
    rememberChoices();
    watchQuoteForm();
    setTimeout(fillMessage, 300);
  });
})();
